/** Header-action builder for the Plant Register screen (/plant).
 *  Two states, mutually exclusive: the register table (search, status filter,
 *  Add) and a drilled-in machine (back chevron, Cancel/Save once something
 *  has changed — Save reads "Create" while the machine is still unsaved).
 *  No separate add/edit form, unlike Employers: the register has too few
 *  required fields to earn one, so create reuses the same always-live panel.
 *  No JSX — every action fires a window event PlantRegisterView listens for. */

import { Check, Loader2, Plus, Search, SlidersHorizontal } from '@/shared/ui/icons';
import {
  PLANT_CLEAR_SELECTION_EVENT,
  PLANT_CREATE_EVENT,
  PLANT_DETAIL_CANCEL_EVENT,
  PLANT_DETAIL_SAVE_EVENT,
  PLANT_FILTER_EVENT,
  plantFilterOptions,
  PLANT_STATUS_FILTER_ALL,
  type PlantDetailState,
  type PlantStatusFilter,
} from '../../../features/plant/constants';
import type { MobileHeaderAction } from './types';

export interface PlantHeaderCtx {
  /** The table is on screen — no machine drilled in. */
  listVisible: boolean;
  /** Plant number of the drilled-in machine, or "New machine" while creating,
   *  or null. Present means the detail panel owns the content area. */
  selectedLabel: string | null;
  statusFilter: PlantStatusFilter;
  examDueCount: number;
  detailState: PlantDetailState | null;
  onSelectStatus: (value: PlantStatusFilter) => void;
  onToggleSearch: () => void;
  leadingActions: MobileHeaderAction[];
  trailingActions: MobileHeaderAction[];
  pushNotificationsAction: () => void;
  pushBackAction: (targetView: string, label: string, onClickOverride?: () => void) => void;
}

const dispatch = (name: string) => window.dispatchEvent(new CustomEvent(name));

/** Appends Plant Register pills to the caller's leading/trailing action
 *  arrays. Called only when `currentView === 'plant'`. */
export function buildPlantHeaderActions(ctx: PlantHeaderCtx): void {
  const {
    listVisible,
    selectedLabel,
    statusFilter,
    examDueCount,
    detailState,
    onSelectStatus,
    onToggleSearch,
    leadingActions,
    trailingActions,
    pushNotificationsAction,
    pushBackAction,
  } = ctx;

  if (selectedLabel) {
    pushBackAction('plant', 'Back to plant register', () => dispatch(PLANT_CLEAR_SELECTION_EVENT));

    if (detailState) {
      if (detailState.hasChanges && !detailState.isSubmitting) {
        // Text pill, never an X — pairs with the Save text pill beside it.
        trailingActions.push({
          id: 'plant-detail-cancel',
          label: 'Cancel',
          ariaLabel: 'Discard changes',
          onClick: () => dispatch(PLANT_DETAIL_CANCEL_EVENT),
          group: 'page',
        });
      }
      const committing = detailState.hasChanges || detailState.isSubmitting || detailState.isCreating;
      if (committing) {
        trailingActions.push({
          id: 'plant-detail-save',
          icon: detailState.isSubmitting ? Loader2 : undefined,
          label: detailState.isSubmitting ? 'Saving' : detailState.isCreating ? 'Create' : 'Save',
          ariaLabel: detailState.isSubmitting
            ? 'Saving plant register'
            : detailState.isCreating ? 'Create machine' : 'Save changes',
          disabled: detailState.isSubmitting || (!detailState.hasChanges && !detailState.isCreating),
          onClick: () => dispatch(PLANT_DETAIL_SAVE_EVENT),
          group: 'page',
        });
      }
    }
    pushNotificationsAction();
    return;
  }

  if (listVisible) {
    leadingActions.push({
      id: 'plant-search',
      icon: Search,
      ariaLabel: 'Search plant register',
      onClick: onToggleSearch,
    });
    const options = plantFilterOptions(examDueCount);
    const active = options.find(o => o.value === statusFilter);
    trailingActions.push({
      // Trailing, group 'view': the filter configures how the register is
      // shown, not navigation — see the leading-edge closed enumeration note
      // in architecture.rules.md.
      id: 'plant-filter',
      icon: SlidersHorizontal,
      label: active?.label ?? 'All Plant',
      ariaLabel: 'Filter plant register by status',
      group: 'view',
      subActions: options.map(opt => ({
        id: `plant-filter-${opt.value}`,
        label: opt.label,
        active: (statusFilter ?? PLANT_STATUS_FILTER_ALL) === opt.value,
        trailingIcon: statusFilter === opt.value ? Check : undefined,
        onClick: () => onSelectStatus(opt.value),
      })),
    });
    trailingActions.push({
      id: 'plant-create',
      icon: Plus,
      ariaLabel: 'Add machine',
      onClick: () => dispatch(PLANT_CREATE_EVENT),
      group: 'create',
    });
  }
  pushNotificationsAction();
}
